import type { CSSProperties } from "react";

import { funEmoji } from "@dicebear/collection";
import { createAvatar } from "@dicebear/core";

export const getAvatarUri = (
  seed: string,
  eyes: (
    | "sad"
    | "tearDrop"
    | "pissed"
    | "cute"
    | "wink"
    | "wink2"
    | "plain"
    | "glasses"
    | "closed"
    | "love"
    | "stars"
    | "shades"
    | "closed2"
    | "crying"
    | "sleepClose"
  )[] = ["plain", "wink", "closed", "closed2"],
  mouth: (
    | "sad"
    | "pissed"
    | "cute"
    | "plain"
    | "smileLol"
    | "smileTeeth"
    | "wideSmile"
    | "lilSmile"
    | "shy"
    | "shout"
    | "drip"
    | "tongueOut"
    | "kissHeart"
    | "sick"
    | "faceMask"
  )[] = ["cute", "smileLol", "smileTeeth", "wideSmile", "lilSmile"],
) => {
  const avatar = createAvatar(funEmoji, {
    seed: seed,
    mouth: mouth,
    eyes,
    backgroundType: ["solid"],
    backgroundColor: ["red"],
  });

  return avatar.toDataUri();
};

export const unAssignedAvatarUri = getAvatarUri(
  "unassigned",
  ["sleepClose"],
  ["plain"],
);

const hashSeed = (seed: string): number => {
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }

  return hash;
};

// A fixed ring rather than the full 0-360 range: keeps neighbouring colors
// visually distinct and skips the muddy yellow-green band. Exported so other
// identity-like UI (such as activity categories) can share the same readable,
// Apple-like pastel system instead of inventing isolated Tailwind colors.
export const PASTEL_COLOR_HUES = {
  coral: 12,
  orange: 42,
  lime: 96,
  green: 152,
  cyan: 196,
  blue: 232,
  purple: 274,
  pink: 318,
} as const;

export type PastelColorName = keyof typeof PASTEL_COLOR_HUES;

export const getPastelColorStyle = (hue: number): CSSProperties => {
  const toHue = (hue + 26) % 360;

  return {
    background: `linear-gradient(135deg, oklch(0.93 0.05 ${hue}), oklch(0.87 0.072 ${toHue}))`,
    color: `oklch(0.42 0.094 ${hue})`,
  };
};

export const getNamedPastelColorStyle = (
  color: PastelColorName,
): CSSProperties => getPastelColorStyle(PASTEL_COLOR_HUES[color]);

// --accent's hue. Avatars sit beside the accent blue, so their hues are read
// off the colour wheel from it: the blue, its neighbours either side, a teal a
// step further round, and its complement amber.
const ACCENT_HUE = 254;
const AVATAR_HUES = [
  ACCENT_HUE,
  ACCENT_HUE - 30,
  ACCENT_HUE + 30,
  ACCENT_HUE - 60,
  ACCENT_HUE - 180,
];

/**
 * Deterministic identity style for an `<Avatar.Fallback>`: a pastel fill with
 * the initials in a deep tone of the same hue. The two halves must stay paired -
 * a hardcoded white or black `color` against the pastel fill drops to ~1.4:1.
 */
export const getAvatarFallbackStyle = (seed: string): CSSProperties => {
  const hue = AVATAR_HUES[hashSeed(seed) % AVATAR_HUES.length];

  return getPastelColorStyle(hue);
};
